/**
 * The panel scopes mounted in this process, and which of them owns a request.
 *
 * Two panel contexts can be alive at once and they must not share a default:
 *
 *   - the **takeover** scope — a device configured as a panel, where the panel
 *     surface IS the window (unchanged for every wall panel in the field);
 *   - an **app** scope — a panel surface opened as a miniapp beside a normal
 *     session, which owns its credential and declares it per request.
 *
 * The registry is a plain module (no pinia) so the API layer can ask "who owns
 * this request" without importing a store: the stores *register* their scope
 * here (push), and the fallback below synthesises the takeover scope before the
 * panel store has restored, so the first paint of `/panel` already carries the
 * right credential.
 */
import { isPanelDeviceTakeover, readStoredPanelCredential } from '../config/panelDevice';
import type { PanelScope } from './apiCredential';

export const TAKEOVER_SCOPE_ID = 'takeover';

const mounted = new Map<string, PanelScope>();

/** A panel surface mounts: it becomes one of the scopes on this wire. */
export function registerPanelScope(scope: PanelScope): void {
  mounted.set(scope.id, { ...scope });
}

/** The panel credential rotated / a pairing completed: the scope follows it. */
export function updatePanelScope(scopeId: string, patch: Partial<Omit<PanelScope, 'id'>>): PanelScope | null {
  const current = mounted.get(scopeId);
  if (!current) return null;
  const next = { ...current, ...patch, id: current.id };
  mounted.set(scopeId, next);
  return next;
}

/** A panel surface unmounts (unpair, close, exit): its credential leaves with it. */
export function unregisterPanelScope(scopeId: string): void {
  mounted.delete(scopeId);
}

export function mountedPanelScopes(): PanelScope[] {
  return [...mounted.values()];
}

export function panelScope(scopeId: string): PanelScope | null {
  return mounted.get(scopeId) ?? null;
}

/** The window-level panel scope, if this window IS a panel. */
export function takeoverPanelScope(): PanelScope | null {
  for (const scope of mounted.values()) {
    if (scope.mode === 'takeover') return scope;
  }
  return null;
}

/** Every miniapp scope currently mounted (there may be more than one). */
export function appPanelScopes(): PanelScope[] {
  return [...mounted.values()].filter((scope) => scope.mode === 'app');
}

/** Test/teardown helper: forget every mounted scope. */
export function clearPanelScopes(): void {
  mounted.clear();
}

/**
 * The scope that owns requests from the whole window — `null` unless this device
 * is a panel that took the window over.
 *
 * Deliberately NOT "a panel credential exists somewhere": a stale token (or a
 * stale `touch_panel_mode` flag) must not make an ordinary app answer as a panel.
 * The claim comes from the explicit device configuration
 * (`config/panelDevice.ts`), and the credential is only used once that claim is
 * made.
 */
export function resolveWindowPanelScope(): PanelScope | null {
  const registered = takeoverPanelScope();
  if (registered) return registered;

  // First paint of a takeover device: the panel store has not restored yet, so
  // build the scope from the same two facts the store will restore from.
  if (!isPanelDeviceTakeover()) return null;
  const credential = readStoredPanelCredential();
  if (!credential) return null;
  return {
    id: TAKEOVER_SCOPE_ID,
    mode: 'takeover',
    credential,
    panelId: null,
    role: 'panel',
  };
}
